import { useEffect, useRef, useState } from 'react';

import {
  faBarsFilter,
  faCheck,
  faClose,
  faTrash,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faBookmark } from '@fortawesome/free-regular-svg-icons';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/videa-components/src/index';
import { cn } from '@repo/videa-components/src/lib/utils';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/videa-components/src/ui/dialog';
import { Input } from '@repo/videa-components/src/ui/input';
import { Label } from '@repo/videa-components/src/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components/src/ui/select';
import { Switch } from '@repo/videa-components/src/ui/switch';
import { isValid, subWeeks } from 'date-fns';
import { useAtom } from 'jotai';
import { useHistory, useLocation } from 'react-router-dom';

import {
  useCreateInsightsSavedView,
  useDeleteInsightsSavedView,
  useGetInsightsSavedViews,
  useUpdateInsightsSavedView,
} from '@/react-query/insights/useInsightsSavedViews';
import type { TDateRangePresetKey } from '@/react-query/insights/useInsightsSavedViews';

import { useLocalizedFormatDate } from '../../../../claims/freeClaims/state';
import type { ComparisonPeriodMode } from '../../atoms';
import {
  DEFAULT_MATERIALITY_FILTER_COUNTS,
  DEFAULT_MATERIALITY_FILTER_DOLLARS,
  DEFAULT_MATERIALITY_FILTER_OPPORTUNITY_COUNTS,
  DEFAULT_MATERIALITY_FILTER_OPPORTUNITY_DOLLARS,
  comparisonPeriodModeAtom,
  customPriorEndDateAtom,
  customPriorStartDateAtom,
  endDateAtom,
  isCountsAtom,
  materialityFilterOpportunityCountsAtom,
  materialityFilterOpportunityDollarsAtom,
  materialityFilterProductionCountsAtom,
  materialityFilterProductionDollarsAtom,
  selectedPracticeGroupsAtom,
  selectedPracticesAtom,
  selectedProviderRoleAtom,
  startDateAtom,
  taskTypeAtom,
  txTypeAtom,
} from '../../atoms';
import {
  getMatchingDatePreset,
  getRangeForPreset,
  isAllowedDateRangePreset,
  isValidPriorPeriod,
  useDatePresetLabels,
  useDateRangePresetOptions,
} from '../../helpers/datePresets';

export const SavedViews = () => {
  const { t } = useLingui();
  const formatDate = useLocalizedFormatDate();
  const { options: dateRangePresetOptions } = useDateRangePresetOptions();
  const datePresetLabels = useDatePresetLabels();
  const [name, setName] = useState<string>('');
  const [isRelativeDateRange, setIsRelativeDateRange] = useState(false);
  const [relativeDatePreset, setRelativeDatePreset] =
    useState<TDateRangePresetKey>('last30');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const hasPrepopulatedPresetRef = useRef(false);

  const [selectedPractices, setSelectedPractices] = useAtom(
    selectedPracticesAtom,
  );
  const [selectedRegions, setSelectedRegions] = useAtom(
    selectedPracticeGroupsAtom,
  );
  const [selectedProviderRole, setSelectedProviderRole] = useAtom(
    selectedProviderRoleAtom,
  );
  const [selectedTaskType, setSelectedTaskType] = useAtom(taskTypeAtom);
  const [selectedTxType, setSelectedTxType] = useAtom(txTypeAtom);
  const [
    selectedMaterialityFilterProductionDollars,
    setSelectedMaterialityFilterProductionDollars,
  ] = useAtom(materialityFilterProductionDollarsAtom);
  const [
    selectedMaterialityFilterProductionCounts,
    setSelectedMaterialityFilterProductionCounts,
  ] = useAtom(materialityFilterProductionCountsAtom);
  const [
    selectedMaterialityFilterOpportunityDollars,
    setSelectedMaterialityFilterOpportunityDollars,
  ] = useAtom(materialityFilterOpportunityDollarsAtom);
  const [
    selectedMaterialityFilterOpportunityCounts,
    setSelectedMaterialityFilterOpportunityCounts,
  ] = useAtom(materialityFilterOpportunityCountsAtom);
  const [isCounts, setIsCounts] = useAtom(isCountsAtom);
  const [startDate, setStartDate] = useAtom(startDateAtom);
  const [endDate, setEndDate] = useAtom(endDateAtom);
  const [comparisonPeriodMode, setComparisonPeriodMode] = useAtom(
    comparisonPeriodModeAtom,
  );
  const [priorStartDate, setPriorStartDate] = useAtom(customPriorStartDateAtom);
  const [priorEndDate, setPriorEndDate] = useAtom(customPriorEndDateAtom);

  const { data: savedViews } = useGetInsightsSavedViews();

  const { mutate: deleteSavedView } = useDeleteInsightsSavedView();

  const { mutate: createSavedView } = useCreateInsightsSavedView();

  const { mutate: updateSavedView } = useUpdateInsightsSavedView();

  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  const selectedDashboard = location.pathname.includes('hygiene')
    ? 'hygiene'
    : 'clinical';
  const providerDrilldown = location.pathname.includes('provider/')
    ? location.pathname.split('provider/')[1]?.split('/')[0]
    : undefined;
  const practiceDrilldown = location.pathname.includes('practice/')
    ? location.pathname.split('practice/')[1]?.split('/')[0]
    : undefined;
  const regionDrilldown = location.pathname.includes('region/')
    ? location.pathname.split('region/')[1]?.split('/')[0]
    : undefined;

  const selectedSavedView = params.get('view');
  const savedView = savedViews?.data.find((v) => v.id === selectedSavedView);

  const isDatesMatchingPreset =
    isRelativeDateRange &&
    getMatchingDatePreset(startDate, endDate)?.value === relativeDatePreset;

  const viewConfig = {
    practices: selectedPractices,
    regions: selectedRegions,
    providerRole: selectedProviderRole,
    taskType: selectedTaskType,
    txType: selectedTxType,
    materialityFilterProductionDollars:
      selectedMaterialityFilterProductionDollars,
    materialityFilterProductionCounts:
      selectedMaterialityFilterProductionCounts,
    materialityFilterOpportunityDollars:
      selectedMaterialityFilterOpportunityDollars,
    materialityFilterOpportunityCounts:
      selectedMaterialityFilterOpportunityCounts,
    isCounts: isCounts,
    ...(isDatesMatchingPreset
      ? {
          relativeDateRange: true,
          dateRangePreset: relativeDatePreset,
          startDate: undefined,
          endDate: undefined,
        }
      : {
          relativeDateRange: false,
          startDate: startDate,
          endDate: endDate,
        }),
    comparisonPeriodMode,
    priorStartDate: priorStartDate ?? undefined,
    priorEndDate: priorEndDate ?? undefined,
    selectedDashboard: selectedDashboard,
    providerDrilldown: providerDrilldown,
    practiceDrilldown: practiceDrilldown,
    regionDrilldown: regionDrilldown,
  };

  const handleClearView = () => {
    setSelectedPractices([]);
    setSelectedRegions([]);
    setSelectedProviderRole([]);
    setSelectedTaskType([]);
    setSelectedTxType([]);

    setSelectedMaterialityFilterProductionDollars(
      DEFAULT_MATERIALITY_FILTER_DOLLARS,
    );
    setSelectedMaterialityFilterProductionCounts(
      DEFAULT_MATERIALITY_FILTER_COUNTS,
    );

    setSelectedMaterialityFilterOpportunityDollars(
      DEFAULT_MATERIALITY_FILTER_OPPORTUNITY_DOLLARS,
    );
    setSelectedMaterialityFilterOpportunityCounts(
      DEFAULT_MATERIALITY_FILTER_OPPORTUNITY_COUNTS,
    );

    setIsCounts(false);
    setStartDate(new Date(subWeeks(new Date(), 8)));
    setEndDate(new Date());
    setComparisonPeriodMode('trailing' as ComparisonPeriodMode);
    setPriorStartDate(null);
    setPriorEndDate(null);
    setName('');
    setIsRelativeDateRange(false);
    setRelativeDatePreset('last30');
  };

  const handleCreateSavedView = () => {
    if (selectedSavedView) {
      if (isRelativeDateRange && !isDatesMatchingPreset) {
        setIsRelativeDateRange(false);
      }
      updateSavedView({
        id: selectedSavedView,
        name,
        view: viewConfig,
      });
    } else {
      createSavedView({
        name,
        view: viewConfig,
      });
    }
  };

  const handleSelectSavedView = (viewId: string) => {
    const view = savedViews?.data.find((v) => v.id === viewId);
    if (view && view.id !== selectedSavedView) {
      const path =
        location.pathname.split('insights')[0] +
        'insights/' +
        (view.view?.selectedDashboard ?? 'clinical') +
        (view?.view?.regionDrilldown ||
        view?.view?.practiceDrilldown ||
        view?.view?.providerDrilldown
          ? `/region/${view?.view?.regionDrilldown}`
          : '') +
        (view?.view?.practiceDrilldown || view?.view?.providerDrilldown
          ? `/practice/${view?.view?.practiceDrilldown}`
          : '') +
        (view?.view?.providerDrilldown
          ? `/provider/${view?.view?.providerDrilldown}`
          : '');

      params.set('view', view.id);
      history.push({ pathname: path, search: params.toString() });
      setName(view.name);
    } else if (view && view.id === selectedSavedView) {
      handleClearView();
      params.delete('view');
      history.push({ pathname: location.pathname, search: params.toString() });
    }
  };

  useEffect(() => {
    if (savedView) {
      setSelectedPractices(savedView?.view?.practices ?? []);
      setSelectedRegions(savedView?.view?.regions ?? []);
      setSelectedProviderRole(savedView?.view?.providerRole ?? []);
      setSelectedTaskType(savedView?.view?.taskType ?? []);
      setSelectedTxType(savedView?.view?.txType ?? []);

      setSelectedMaterialityFilterProductionDollars(
        savedView?.view?.materialityFilterProductionDollars ??
          DEFAULT_MATERIALITY_FILTER_DOLLARS,
      );
      setSelectedMaterialityFilterProductionCounts(
        savedView?.view?.materialityFilterProductionCounts ??
          DEFAULT_MATERIALITY_FILTER_COUNTS,
      );

      setSelectedMaterialityFilterOpportunityDollars(
        savedView?.view?.materialityFilterOpportunityDollars ??
          DEFAULT_MATERIALITY_FILTER_OPPORTUNITY_DOLLARS,
      );
      setSelectedMaterialityFilterOpportunityCounts(
        savedView?.view?.materialityFilterOpportunityCounts ??
          DEFAULT_MATERIALITY_FILTER_OPPORTUNITY_COUNTS,
      );

      setIsCounts(!!savedView?.view?.isCounts);

      // Date range: relative (recalc from today) or absolute (stored dates)
      let startDateLoaded: Date;
      let endDateLoaded: Date;
      if (savedView?.view?.relativeDateRange) {
        const presetKey = isAllowedDateRangePreset(
          savedView.view.dateRangePreset,
        )
          ? (savedView.view.dateRangePreset as TDateRangePresetKey)
          : 'last30';
        const range = getRangeForPreset(presetKey);
        startDateLoaded = range.start;
        endDateLoaded = range.end;
        setStartDate(startDateLoaded);
        setEndDate(endDateLoaded);
        setIsRelativeDateRange(true);
        setRelativeDatePreset(presetKey);
      } else {
        startDateLoaded =
          savedView?.view?.startDate &&
          isValid(new Date(savedView?.view?.startDate))
            ? new Date(savedView.view.startDate)
            : new Date(subWeeks(new Date(), 8));
        endDateLoaded =
          savedView?.view?.endDate &&
          isValid(new Date(savedView?.view?.endDate))
            ? new Date(savedView.view.endDate)
            : new Date();
        setStartDate(startDateLoaded);
        setEndDate(endDateLoaded);
        setIsRelativeDateRange(false);
      }

      // Comparison period mode and prior dates (validate custom prior is before current)
      let mode: 'trailing' | 'yoy' | 'custom' =
        savedView?.view?.comparisonPeriodMode === 'yoy' ||
        savedView?.view?.comparisonPeriodMode === 'custom'
          ? savedView.view.comparisonPeriodMode
          : 'trailing';

      let priorStart: Date | null = null;
      let priorEnd: Date | null = null;
      if (
        savedView?.view?.priorStartDate &&
        isValid(new Date(savedView.view.priorStartDate)) &&
        savedView?.view?.priorEndDate &&
        isValid(new Date(savedView.view.priorEndDate))
      ) {
        priorStart = new Date(savedView.view.priorStartDate);
        priorEnd = new Date(savedView.view.priorEndDate);
      }

      if (
        mode === 'custom' &&
        (priorStart == null ||
          priorEnd == null ||
          !isValidPriorPeriod(priorStart, priorEnd, startDateLoaded))
      ) {
        mode = 'trailing';
        priorStart = null;
        priorEnd = null;
      }

      setComparisonPeriodMode(mode);
      setPriorStartDate(priorStart);
      setPriorEndDate(priorEnd);
      setName(savedView?.name ?? '');
    } else {
      // No saved view selected: reset dialog date options so they don't persist from a cleared view
      setIsRelativeDateRange(false);
      setRelativeDatePreset('last30');
    }
  }, [
    savedView,
    setSelectedPractices,
    setSelectedRegions,
    setSelectedProviderRole,
    setSelectedTaskType,
    setSelectedTxType,
    setSelectedMaterialityFilterProductionDollars,
    setSelectedMaterialityFilterProductionCounts,
    setSelectedMaterialityFilterOpportunityDollars,
    setSelectedMaterialityFilterOpportunityCounts,
    setIsCounts,
    setStartDate,
    setEndDate,
    setComparisonPeriodMode,
    setPriorStartDate,
    setPriorEndDate,
    setName,
    setIsRelativeDateRange,
    setRelativeDatePreset,
  ]);

  // When dialog opens for Create (no saved view), pre-populate Relative dates if current range matches a preset
  useEffect(() => {
    if (!isDialogOpen) {
      hasPrepopulatedPresetRef.current = false;
      return;
    }
    if (selectedSavedView) return;
    if (hasPrepopulatedPresetRef.current) return;
    hasPrepopulatedPresetRef.current = true;
    const match = getMatchingDatePreset(startDate, endDate);
    if (match) {
      setIsRelativeDateRange(true);
      setRelativeDatePreset(match.value);
    }
  }, [isDialogOpen, selectedSavedView, startDate, endDate]);

  return (
    <div className="vai-flex vai-flex-row vai-gap-2">
      {selectedSavedView && (
        <Button
          variant="ghost"
          onClick={() => {
            handleClearView();
            params.delete('view');
            history.push({
              pathname: location.pathname,
              search: params.toString(),
            });
          }}
        >
          <FontAwesomeIcon icon={faClose} />
          <span className="vai-text-sm vai-font-normal">
            <Trans>Clear</Trans>
          </span>
        </Button>
      )}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline">
            <FontAwesomeIcon icon={faBookmark} />
            <span className="vai-text-sm vai-font-normal">
              <Trans>Save</Trans>
            </span>
          </Button>
        </DialogTrigger>
        <DialogContent className="vai-max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedSavedView ? (
                <Trans>Update Custom View</Trans>
              ) : (
                <Trans>Create Custom View</Trans>
              )}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="vai-flex vai-flex-col vai-gap-4">
            <div className="vai-flex vai-flex-col vai-gap-2">
              <Label>
                <Trans>Name</Trans>
              </Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="focus-visible:vai-ring-0"
              />
            </div>
            <div className="vai-flex vai-flex-col vai-gap-2">
              <Label className="vai-text-sm vai-font-medium">
                <Trans>Date range</Trans>
              </Label>
              <div className="vai-flex vai-items-center vai-justify-center vai-gap-3">
                <span
                  className={cn(
                    'vai-text-sm',
                    !isRelativeDateRange &&
                      'vai-font-medium vai-text-foreground',
                    isRelativeDateRange && 'vai-text-muted-foreground',
                  )}
                >
                  <Trans>Fixed dates</Trans>
                </span>
                <Switch
                  checked={isRelativeDateRange}
                  onCheckedChange={setIsRelativeDateRange}
                  aria-label={t`Use relative dates (preset from today)`}
                />
                <span
                  className={cn(
                    'vai-text-sm',
                    isRelativeDateRange &&
                      'vai-font-medium vai-text-foreground',
                    !isRelativeDateRange && 'vai-text-muted-foreground',
                  )}
                >
                  <Trans>Relative dates</Trans>
                </span>
              </div>
              <p className="vai-text-xs vai-text-muted-foreground">
                {isRelativeDateRange ? (
                  <Trans>
                    Dates update from today whenever you open this view.
                  </Trans>
                ) : (
                  <Trans>Use the exact dates currently in the view.</Trans>
                )}
              </p>
              {isRelativeDateRange && (
                <div className="vai-flex vai-flex-col vai-gap-1.5">
                  <Label className="vai-text-xs vai-text-muted-foreground">
                    <Trans>Date Range Preset</Trans>
                  </Label>
                  <Select
                    value={relativeDatePreset}
                    onValueChange={(value) => {
                      const preset = value as TDateRangePresetKey;
                      setRelativeDatePreset(preset);
                      const range = getRangeForPreset(preset);
                      setStartDate(range.start);
                      setEndDate(range.end);
                    }}
                  >
                    <SelectTrigger className="vai-w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {dateRangePresetOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {datePresetLabels[opt.value]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </DialogDescription>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">
                <Trans>Cancel</Trans>
              </Button>
            </DialogClose>
            <DialogClose asChild>
              <Button
                variant="default"
                disabled={!name.length}
                onClick={handleCreateSavedView}
              >
                <Trans>Save</Trans>
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="vai-justify-between vai-text-left vai-font-normal"
          >
            <FontAwesomeIcon icon={faBarsFilter} />
            <span className="vai-text-sm vai-font-normal">
              {selectedSavedView
                ? savedViews?.data.find((view) => view.id === selectedSavedView)
                    ?.name
                : t`Custom Views`}
            </span>
            <FontAwesomeIcon
              className="vai-text-[#71717A] vai-flex-shrink-0"
              icon={faAngleDown}
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="vai-max-h-[65vh] vai-overflow-y-auto vai-w-96"
          zIndex={1}
        >
          {savedViews?.data.length ? (
            savedViews?.data?.map((view) => (
              <DropdownMenuItem
                key={view.id}
                onClick={(e) => {
                  e.preventDefault();
                }}
              >
                <div className="vai-flex vai-gap-4 vai-items-center vai-justify-between vai-w-full">
                  <button
                    className="vai-flex vai-gap-4 vai-items-center"
                    onClick={() => handleSelectSavedView(view.id)}
                  >
                    <FontAwesomeIcon
                      icon={faCheck}
                      className={cn(
                        selectedSavedView !== view.id && 'vai-opacity-0',
                      )}
                    />

                    <div className="vai-flex vai-flex-col vai-items-start">
                      <span className="vai-max-w-[17rem] vai-text-sm vai-text-left vai-font-normal vai-text-ellipsis vai-overflow-hidden vai-whitespace-nowrap">
                        {view.name}
                      </span>
                      <span className="vai-max-w-[17rem] vai-text-[12px] vai-font-normal vai-text-left vai-text-muted-foreground vai-text-ellipsis vai-overflow-hidden vai-whitespace-nowrap">
                        {t`Updated By`}: {view.updatedBy} •{' '}
                        {formatDate(view.updatedAt, false, 'PP')}
                      </span>
                    </div>
                  </button>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <FontAwesomeIcon
                          icon={faTrash}
                          className="vai-text-muted-foreground"
                        />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="vai-max-w-lg">
                      <DialogHeader>
                        <DialogTitle>
                          <Trans>Delete Custom View?</Trans>
                        </DialogTitle>
                      </DialogHeader>
                      <DialogDescription>
                        <Trans>
                          This custom view will be permanently deleted and will
                          no longer be available to any users in your
                          organization. Are you sure you want to remove it?
                        </Trans>
                      </DialogDescription>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline">
                            <Trans>Go Back</Trans>
                          </Button>
                        </DialogClose>
                        <DialogClose asChild>
                          <Button
                            variant="destructive"
                            onClick={() => {
                              deleteSavedView({ id: view.id });
                              if (view.id === selectedSavedView) {
                                params.delete('view');

                                history.push({
                                  pathname: location.pathname,
                                  search: params.toString(),
                                });
                              }
                            }}
                          >
                            <Trans>Remove View</Trans>
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </DropdownMenuItem>
            ))
          ) : (
            <DropdownMenuItem className="vai-text-muted-foreground vai-flex vai-justify-end">
              <Trans>No custom views</Trans>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
